import React, { useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
  FileText, Package, Upload, GitBranch, ArrowLeft, Loader2, CheckCircle,
  Shield, Scale, AlertTriangle, ExternalLink, Search, Database
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { generateSBOM, getEcosystemIcon } from '@/services/sbom';

const SBOMGeneratePage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Determine base path from current location
  const basePath = location.pathname.startsWith('/litesabre') && !location.pathname.startsWith('/litesabre-pro') 
    ? '/litesabre' 
    : '/grc/litesabre';
  const [searchParams] = useSearchParams();
  const scanId = searchParams.get('scanId');
  
  const [name, setName] = useState('');
  const [target, setTarget] = useState('');
  const [ecosystem, setEcosystem] = useState('');
  const [includeDevDeps, setIncludeDevDeps] = useState(true);
  const [includeTransitive, setIncludeTransitive] = useState(true);
  const [checkLicenses, setCheckLicenses] = useState(true);
  const [checkVulnerabilities, setCheckVulnerabilities] = useState(true);
  const [detectProprietaryLicenses, setDetectProprietaryLicenses] = useState(true);
  const [generating, setGenerating] = useState(false);

  const ecosystems = [
    { value: 'npm', label: 'npm (Node.js)', icon: '📦' },
    { value: 'pypi', label: 'PyPI (Python)', icon: '🐍' },
    { value: 'maven', label: 'Maven (Java)', icon: '☕' },
    { value: 'go', label: 'Go Modules', icon: '🐹' },
    { value: 'cargo', label: 'Cargo (Rust)', icon: '🦀' },
    { value: 'rubygems', label: 'RubyGems', icon: '💎' },
    { value: 'nuget', label: 'NuGet (.NET)', icon: '🔷' },
    { value: 'composer', label: 'Composer (PHP)', icon: '🎼' },
  ];

  // Open source dependency tools/databases
  const dependencyTools = [
    { name: 'OSS Index (Sonatype)', icon: '🔍', desc: 'Vulnerability database', enabled: true },
    { name: 'NVD (NIST)', icon: '🏛️', desc: 'National Vulnerability Database', enabled: true },
    { name: 'GitHub Advisory', icon: '🐙', desc: 'GitHub Security Advisories', enabled: true },
    { name: 'OSV.dev', icon: '🌐', desc: 'Open Source Vulnerabilities', enabled: true },
    { name: 'Snyk DB', icon: '🔒', desc: 'Snyk Vulnerability DB', enabled: false },
    { name: 'EPSS', icon: '📊', desc: 'Exploit Prediction Scoring', enabled: true },
  ];

  // License categories for proprietary detection
  const licenseCategories = [
    { type: 'permissive', label: 'Permissive', examples: 'MIT, Apache-2.0, BSD', color: 'green' },
    { type: 'copyleft', label: 'Copyleft', examples: 'GPL, LGPL, AGPL', color: 'amber' },
    { type: 'proprietary', label: 'Proprietary', examples: 'Commercial, Custom EULA', color: 'red' },
    { type: 'unknown', label: 'Unknown', examples: 'No license detected', color: 'gray' },
  ];

  const handleGenerate = async () => {
    if (!name.trim()) {
      toast.error('Please enter a name for the SBOM');
      return;
    }
    if (!target.trim()) {
      toast.error('Please enter a target (file path, repo URL, or project name)');
      return;
    }
    
    setGenerating(true);
    try {
      const result = await generateSBOM({
        name: name.trim(),
        target: target.trim(),
        ecosystem: ecosystem || null,
        scan_id: scanId || null,
        include_dev_dependencies: includeDevDeps,
        include_transitive: includeTransitive,
        check_licenses: checkLicenses,
        check_vulnerabilities: checkVulnerabilities,
        detect_proprietary_licenses: detectProprietaryLicenses
      });
      
      toast.success('SBOM generated successfully!');
      navigate(`${basePath}/sbom/${result.id}`);
    } catch (err) {
      console.error('Failed to generate SBOM:', err);
      toast.error(err.response?.data?.detail || 'Failed to generate SBOM');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(`${basePath}/sbom`)}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="w-7 h-7 text-indigo-600" />
            Generate SBOM
          </h1>
          <p className="text-gray-500 mt-1">
            Create a Software Bill of Materials for your project
          </p>
        </div>
      </div>

      {scanId && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="py-4">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-blue-600" />
              <span className="text-blue-700">
                Generating SBOM from scan: <code className="font-mono">{scanId}</code>
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Form */}
      <Card>
        <CardHeader>
          <CardTitle>SBOM Details</CardTitle>
          <CardDescription>
            Enter the details for your Software Bill of Materials
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Report Name *</Label>
            <Input
              id="name"
              placeholder="e.g., My App SBOM v1.0"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="sbom-name-input"
            />
          </div>

          {/* Target */}
          <div className="space-y-2">
            <Label htmlFor="target">Target *</Label>
            <Input
              id="target"
              placeholder="e.g., package.json, requirements.txt, or repo URL"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              data-testid="sbom-target-input"
            />
            <p className="text-xs text-gray-500">
              Enter a dependency file name, repository URL, or project identifier
            </p>
          </div>

          {/* Ecosystem */}
          <div className="space-y-2">
            <Label>Ecosystem (Optional)</Label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {ecosystems.map((eco) => (
                <button
                  key={eco.value}
                  type="button"
                  onClick={() => setEcosystem(ecosystem === eco.value ? '' : eco.value)}
                  className={`p-3 rounded-lg border text-left transition-colors ${
                    ecosystem === eco.value
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <span className="text-xl">{eco.icon}</span>
                  <p className="text-sm mt-1 font-medium">{eco.label.split(' ')[0]}</p>
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500">
              Leave empty to auto-detect from target
            </p>
          </div>

          {/* Options */}
          <div className="space-y-4">
            <Label>Dependency Options</Label>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Checkbox
                  id="includeDevDeps"
                  checked={includeDevDeps}
                  onCheckedChange={setIncludeDevDeps}
                />
                <div>
                  <Label htmlFor="includeDevDeps" className="cursor-pointer">
                    Include Development Dependencies
                  </Label>
                  <p className="text-xs text-gray-500">
                    Include devDependencies, test dependencies, etc.
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <Checkbox
                  id="includeTransitive"
                  checked={includeTransitive}
                  onCheckedChange={setIncludeTransitive}
                />
                <div>
                  <Label htmlFor="includeTransitive" className="cursor-pointer">
                    Include Transitive Dependencies
                  </Label>
                  <p className="text-xs text-gray-500">
                    Include indirect dependencies (dependencies of dependencies)
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Security & License Options */}
          <div className="space-y-4">
            <Label>Security & License Analysis</Label>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Checkbox
                  id="checkVulnerabilities"
                  checked={checkVulnerabilities}
                  onCheckedChange={setCheckVulnerabilities}
                />
                <div>
                  <Label htmlFor="checkVulnerabilities" className="cursor-pointer flex items-center gap-2">
                    <Shield className="w-4 h-4 text-blue-600" />
                    Check for Known Vulnerabilities
                  </Label>
                  <p className="text-xs text-gray-500">
                    Cross-reference with NVD, OSS Index, GitHub Advisory, and OSV.dev
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <Checkbox
                  id="checkLicenses"
                  checked={checkLicenses}
                  onCheckedChange={setCheckLicenses}
                />
                <div>
                  <Label htmlFor="checkLicenses" className="cursor-pointer flex items-center gap-2">
                    <Scale className="w-4 h-4 text-purple-600" />
                    Analyze Open Source Licenses
                  </Label>
                  <p className="text-xs text-gray-500">
                    Identify license types: MIT, Apache, GPL, BSD, etc.
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Checkbox
                  id="detectProprietaryLicenses"
                  checked={detectProprietaryLicenses}
                  onCheckedChange={setDetectProprietaryLicenses}
                />
                <div>
                  <Label htmlFor="detectProprietaryLicenses" className="cursor-pointer flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                    Detect Proprietary & Restrictive Licenses
                  </Label>
                  <p className="text-xs text-gray-500">
                    Flag commercial, proprietary, or copyleft licenses that may require legal review
                  </p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Open Source Dependency Tools */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="w-5 h-5 text-indigo-600" />
            Vulnerability Data Sources
          </CardTitle>
          <CardDescription>
            Dependencies are checked against these open source vulnerability databases
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {dependencyTools.map((tool, idx) => (
              <div 
                key={idx}
                className={`p-3 rounded-lg border ${
                  tool.enabled 
                    ? 'border-green-200 bg-green-50' 
                    : 'border-gray-200 bg-gray-50 opacity-60'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg">{tool.icon}</span>
                  <span className="font-medium text-sm">{tool.name}</span>
                  {tool.enabled && <CheckCircle className="w-3 h-3 text-green-600" />}
                </div>
                <p className="text-xs text-gray-500">{tool.desc}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* License Categories */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Scale className="w-5 h-5 text-purple-600" />
            License Classification
          </CardTitle>
          <CardDescription>
            Dependencies are categorized by license type for compliance review
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {licenseCategories.map((cat, idx) => (
              <div 
                key={idx}
                className={`p-3 rounded-lg border-2 border-${cat.color}-200 bg-${cat.color}-50`}
              >
                <div className={`font-medium text-${cat.color}-700 mb-1`}>
                  {cat.label}
                </div>
                <p className="text-xs text-gray-600">{cat.examples}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-amber-800">Proprietary License Detection</p>
                <p className="text-amber-700 text-xs mt-1">
                  Components with proprietary, commercial, or custom EULAs will be flagged for legal review. 
                  This includes licenses that may restrict redistribution, require attribution, or have patent clauses.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex justify-end gap-3">
        <Button 
          variant="outline" 
          onClick={() => navigate(`${basePath}/sbom`)}
        >
          Cancel
        </Button>
        <Button
          onClick={handleGenerate}
          disabled={generating || !name.trim() || !target.trim()}
          className="bg-indigo-600 hover:bg-indigo-700"
          data-testid="generate-sbom-submit"
        >
          {generating ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <FileText className="w-4 h-4 mr-2" />
              Generate SBOM
            </>
          )}
        </Button>
      </div>

      {/* Info Card */}
      <Card className="bg-gray-50">
        <CardContent className="py-4">
          <h4 className="font-medium mb-2">About SBOM</h4>
          <p className="text-sm text-gray-600 mb-3">
            A Software Bill of Materials (SBOM) is a comprehensive inventory of all 
            components, libraries, and dependencies used in your software. It helps with:
          </p>
          <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
            <li>Vulnerability tracking and management</li>
            <li>License compliance verification</li>
            <li>Supply chain security assessment</li>
            <li>Regulatory compliance (EO 14028, etc.)</li>
          </ul>
          <p className="text-sm text-gray-500 mt-3">
            Supported export formats: CycloneDX 1.5, SPDX 2.3, CSV, JSON
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default SBOMGeneratePage;
